import React, { useEffect, useRef, useState } from "react";
import CanvasJSReact from "@/assets/canvasjs.react";
import { rainbowColors } from "@/pages/Dashboard/ComparisonTools";
import { colorSchemaList } from "@/utils/colorSchemas";
import { dateFormatForCanvasJS } from "@/utils/globalUtils";
import PropTypes from "prop-types";

var CanvasJS = CanvasJSReact.CanvasJS;
var CanvasJSChart = CanvasJSReact.CanvasJSChart;

const getMaxYValue = (dataPoints) => {
  return Math.max(...dataPoints.map((t) => t.y));
};

const SplineChart = ({
  title,
  subtitle,
  data = [],
  dateFormat = "D/M/YY hh:mm a",
  yAxis,
  showAlarmedDatapoints = false,
  isDark = false,
  lineDashType = "solid",
  gridDashType = "solid",
  legendPosition = "center bottom",
  gridLine = true,
  verticalLine = true,
  markerType,
  markerColor,
  colorSchema,
  threshold = [],
  showThumbsUp = false,
  height,
}) => {
  const [key, setKey] = useState(Date.now());
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);

  const scheme = colorSchemaList.others?.find((item) => item?.value === colorSchema);
  // CanvasJS.addColorSet("splineShades", rainbowColors);
  CanvasJS.addColorSet("splineShades", scheme?.colors || rainbowColors);

  const hideGridLine = {
    lineColor: !verticalLine ? "transparent" : "lightgray", // Hide Y-axis line
    lineThickness: verticalLine ? 1 : 0, // Hide Y-axis line
    gridThickness: 0, // Remove Y-axis lines in the background
    tickLength: 0,
  };

  const showGridLine = {
    gridColor: "lightgray",
    lineColor: "lightgray",
    tickColor: "lightgray",
    gridDashType,
    legendText: gridDashType,
  };

  const fixImagePosition = () => {
    const chart = chartRef.current;
    if (chart && showThumbsUp) {
      data.forEach((item, idx) => {
        const maxYValue = getMaxYValue(item?.data || item?.chart);
        const maxDataPoint = (item?.data || item?.chart || []).find((t) => t.y === maxYValue);
        const imageId = `thumbs-up-marker-${idx}`;

        const existingImg = chartContainerRef.current.querySelector(`#${imageId}`);

        if (maxDataPoint && existingImg) {
          const positionMarkerImage = () => {
            const pixelX = chart.axisX[0].convertValueToPixel(new Date(maxDataPoint.x));
            const pixelY = chart.axisY[0].convertValueToPixel(maxDataPoint.y);

            existingImg.style.left = `${pixelX - 15}px`;
            existingImg.style.top = `${pixelY - 30}px`;
          };

          positionMarkerImage();

          window.addEventListener("resize", positionMarkerImage);

          return () => {
            window.removeEventListener("resize", positionMarkerImage);
            existingImg.remove();
          };
        }
      });
    }
  };

  const rerenderImage = () => {
    const chart = chartRef.current;
    if (chart && showThumbsUp) {
      data.forEach((item, idx) => {
        const maxYValue = getMaxYValue(item?.data || item?.chart);
        const maxDataPoint = (item?.data || item?.chart || []).find((t) => t.y === maxYValue);
        const imageId = `thumbs-up-marker-${idx}`;

        const existingImg = chartContainerRef.current.querySelector(`#${imageId}`);
        if (existingImg) {
          existingImg.remove();
        }
        if (maxDataPoint) {
          const img = document.createElement("img");
          img.src = "/images/like.png";
          img.style.position = "absolute";
          img.style.height = "23px";
          img.style.width = "23px";
          img.style.zIndex = 10;
          // img.classList.add("thumbs-up-marker" + idx);
          img.id = imageId;

          chartContainerRef.current.querySelector(".canvasjs-chart-container").appendChild(img);

          const positionMarkerImage = () => {
            const pixelX = chart.axisX[0].convertValueToPixel(new Date(maxDataPoint.x));
            const pixelY = chart.axisY[0].convertValueToPixel(maxDataPoint.y);

            img.style.left = `${pixelX - 15}px`;
            img.style.top = `${pixelY - 30}px`;
          };

          positionMarkerImage();

          window.addEventListener("resize", positionMarkerImage);
          return () => {
            window.removeEventListener("resize", positionMarkerImage);
            img.remove();
          };
        }
      });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      rerenderImage();
    }, 100);
    setKey(Date.now());
    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener("resize", rerenderImage);
    };
  }, [data, chartRef, showThumbsUp]);

  useEffect(() => {
    fixImagePosition();
  }, [chartContainerRef?.current?.clientHeight, chartContainerRef?.current?.clientWidth]);

  return (
    // <Spin spinning={isLoading}>
    <div
      className="sp-line nodrag nopan nowheel"
      ref={chartContainerRef}
      style={{ position: "relative", width: "100%", height: "100%", cursor: "auto" }}
      key={key}
      onMouseDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      // onResize={() => {
      //   setTimeout(() => {
      //     fixImagePosition();
      //   }, 100);
      // }}
    >
      <CanvasJSChart
        options={{
          colorSet: "splineShades",
          zoomEnabled: true,
          theme: isDark ? "dark2" : "light1",
          // animationEnabled: true,
          // animationDuration: 1000,
          height:
            (height && height > 100 ? height - 35 : null) ||
            (chartContainerRef.current?.clientHeight && chartContainerRef.current.clientHeight > 100
              ? chartContainerRef.current.clientHeight - 30
              : null) ||
            320,
          title: {
            text: title,
            fontSize: 14,
            // fontWeight: "bold",
            color: "#b1adad",
          },
          subtitles: [
            {
              text: subtitle,
            },
          ],
          axisY: {
            includeZero: false,
            stripLines:
              Array.isArray(threshold) && threshold.length
                ? threshold?.map((data) => {
                    if (data) {
                      return { color: data.color, value: data.value };
                    }
                    return {};
                  })
                : [],
            labelFontSize: 12,
            labelFontColor: "#b1adad",
            ...yAxis,
            ...(gridLine ? showGridLine : hideGridLine),
            lineThickness: verticalLine ? 1 : 0,
          },
          axisX: {
            labelWrap: false,
            valueFormatString: dateFormatForCanvasJS(dateFormat),
            labelAngle: 25,
            labelFontSize: 12,
            labelFontColor: "#b1adad",
            ...(gridLine ? showGridLine : hideGridLine),
          },
          legend: {
            cursor: "pointer",
            itemclick: function (e) {
              if (typeof e.dataSeries.visible === "undefined" || e.dataSeries.visible) {
                e.dataSeries.visible = false;

                // Hide the corresponding thumbs-up image
                const imageId = `thumbs-up-marker-${e.dataSeriesIndex}`;
                const img = chartContainerRef.current.querySelector(`#${imageId}`);
                if (img) {
                  img.style.display = "none";
                }
              } else {
                e.dataSeries.visible = true;

                // Show the corresponding thumbs-up image
                const imageId = `thumbs-up-marker-${e.dataSeriesIndex}`;
                const img = chartContainerRef.current.querySelector(`#${imageId}`);
                if (img) {
                  img.style.display = "block";
                }
              }
              e.chart.render();
              fixImagePosition();
            },
            horizontalAlign: legendPosition.split(" ")[0] || "center",
            verticalAlign: legendPosition.split(" ")[1] || "bottom",
            fontSize: 12,
            fontColor: "#b1adad",
          },

          toolTip: {
            shared: true,
            cornerRadius: 4,
          },
          data: [
            ...data.map((d) => {
              return {
                type: "spline",
                name: d.name || d._id,
                xValueFormatString: "DD MMM YYYY, hh:mm TT",
                showInLegend: legendPosition !== "hide",
                lineDashType,
                lineTension: 1,
                dataPoints: (d.data || d.chart || []).map((t) => ({
                  x: new Date(t.x),
                  // x: convertToMalaysiaTime(t.x),
                  y: t.y,
                  markerColor: markerColor
                    ? markerColor
                    : showAlarmedDatapoints
                      ? t.z && t.z !== "normal"
                        ? "red"
                        : "transparent"
                      : "",
                  markerType: markerType || "circle",
                  markerSize: 6,
                  indexLabelPlacement: "outside",
                  indexLabelFontSize: 32,
                  indexLabelFontWeight: "bold",
                })),
              };
            }),
          ],
        }}
        onRef={(chart) => (chartRef.current = chart)}
      />
      {/* </Spin> */}
    </div>
  );
};

export default SplineChart;

// const convertToMalaysiaTime = (dateStr) => {
//   const date = new Date(dateStr); // Convert the string to a Date object
//   const utcOffset = 8 * 60; // Malaysia is UTC+8
//   const localTime = new Date(date.getTime() + utcOffset * 60000); // Adjust the time by adding the UTC offset in milliseconds
//   return localTime;
// };

SplineChart.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  data: PropTypes.array,
  dateFormat: PropTypes.string,
  yAxis: PropTypes.string,
  showAlarmedDatapoints: PropTypes.bool,
  isDark: PropTypes.bool,
  lineDashType: PropTypes.string,
  gridDashType: PropTypes.string,
  legendPosition: PropTypes.string,
  gridLine: PropTypes.bool,
  verticalLine: PropTypes.bool,
  markerType: PropTypes.string,
  markerColor: PropTypes.string,
  colorSchema: PropTypes.string,
  threshold: PropTypes.array,
  showThumbsUp: PropTypes.bool,
};
